import { useSyncExternalStore } from "react";
import type { FinishesSource, FinishesView, FinishSheetSurface } from "./events";
import { getFlag, ROOM_FINISHES_FLAG } from "./flags";
import { beginFinishesSession, endFinishesSession, type FinishesCloseMethod } from "./finishesTelemetry";
import { isRender3dOpen } from "./render3dRequests";
import { useDesign } from "./store";

/**
 * App-wide "open the Finishes sheet" requests. The 3D walk-through, the
 * Walls header chip and the Shape view's Room details all call
 * {@link requestFinishes}. A request opened inside the walk-through renders
 * in the 3D overlay; any other in `FinishesHost` (`App.tsx`). The sheet
 * session (its `finishes_opened` → `finishes_closed` summary) follows the
 * request, not whichever component happens to render it.
 */

/** Where the sheet should land: a card, and for walls, one wall. */
export interface FinishesFocus {
  surface: FinishSheetSurface | null;
  wallKey: string | null;
  /** Increments on every focus request so the same surface can be re-focused. */
  seq: number;
}

export interface FinishesRequest {
  /** Increments per open (a new sheet session). */
  id: number;
  source: FinishesSource;
  view: FinishesView;
  /** Opened inside the 3D walk-through (the overlay renders it). */
  in3d: boolean;
  focus: FinishesFocus;
}

export interface FinishesOpenOptions {
  source: FinishesSource;
  view: FinishesView;
  surface?: FinishSheetSurface | null;
  wallKey?: string | null;
}

let current: FinishesRequest | null = null;
let seq = 0;
let focusSeq = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/**
 * Open the sheet, or (already open) move it to a surface. A focus change
 * keeps the session, so its summary still counts from the first open.
 */
export function requestFinishes(opts: FinishesOpenOptions): void {
  if (!getFlag(ROOM_FINISHES_FLAG)) return;
  const focus = { surface: opts.surface ?? null, wallKey: opts.wallKey ?? null, seq: ++focusSeq };
  if (current) {
    current = { ...current, focus };
    emit();
    return;
  }
  current = { id: ++seq, source: opts.source, view: opts.view, in3d: isRender3dOpen(), focus };
  beginFinishesSession({
    id: current.id,
    source: opts.source,
    view: opts.view,
    surface: focus.surface,
    wallKey: focus.wallKey,
    design: useDesign.getState().design,
  });
  emit();
}

export function closeFinishes(method: FinishesCloseMethod = "dismiss"): void {
  if (!current) return;
  current = null;
  endFinishesSession(method, useDesign.getState().design);
  emit();
}

export function getFinishesRequest(): FinishesRequest | null {
  return current;
}

export function isFinishesOpen(): boolean {
  return current !== null;
}

export function useFinishesRequest(): FinishesRequest | null {
  return useSyncExternalStore(subscribe, () => current, () => null);
}

/** Test hook. */
export function _resetFinishesRequestsForTests(): void {
  current = null;
  seq = 0;
  focusSeq = 0;
  listeners.clear();
}
